import CountdownTimer from "@/components/CountdownTimer";
import EventRegistrationForm from "@/components/EventRegistrationForm";
import { WHATSAPP_URL } from "@/constants/testIds";
import { MessageCircle } from "lucide-react";

/**
 * Standalone EOI landing page — no navigation, single CTA repeated top/middle/bottom.
 * Framed around one pillar based on ?p= param (default: nature).
 */
export default function EOI() {
  const params = new URLSearchParams(window.location.search);
  const framing = params.get("p") || "nature";

  const copy = {
    nature: {
      eyebrow: "Peace with Nature",
      headline: "Above the clouds.\nWhere the mist arrives first.",
      subline: "A 300-acre private hill near Pali. Golden-hour drone footage isn't the pitch — it's the site visit.",
    },
    wealth: {
      eyebrow: "Peace with Wealth",
      headline: "The quiet\ncompounding of land.",
      subline: "NA plots, RERA-registered, inside the Navi Mumbai International Airport and Mumbai 3.0 catchment. A slow, boring, powerful hold.",
    },
    mind: {
      eyebrow: "Peace of Mind",
      headline: "Clear title.\nClear conscience.",
      subline: "Non-Agricultural, Sanad in place, layout registered with MahaRERA. No fine print. No surprises.",
    },
    family: {
      eyebrow: "Peace with Family",
      headline: "The land\nthat is still there\nfor the next visit.",
      subline: "A place your family will remember — because it was the one weekend without a screen.",
    },
    time: {
      eyebrow: "Peace with Time",
      headline: "The two-hour Sunday\nyou didn't know\nyou had.",
      subline: "Two hours from Mumbai. Two hours from Pune. Forty-five minutes off the Expressway.",
    },
  }[framing] || {};

  return (
    <div className="bg-offwhite">
      {/* Minimal header — logo only, no nav */}
      <header className="border-b border-forest/10">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-forest grid place-items-center">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#A6CE39" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 20h18" />
                <path d="M5 20 L10 8 L14 15 L17 10 L21 20" />
              </svg>
            </span>
            <div className="leading-tight">
              <div className="font-display text-forest text-base">Pali Hill City</div>
              <div className="eyebrow text-forest/60 text-[9px]">Chapter III</div>
            </div>
          </div>
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="text-forest text-sm inline-flex items-center gap-2">
            <MessageCircle size={16} /> WhatsApp
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[85vh] bg-void overflow-hidden flex items-center">
        <img
          src="https://images.unsplash.com/photo-1470770841072-f978cf4d019e?w=2000&q=85&auto=format&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/15" />
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 py-24 text-cream w-full">
          <div className="max-w-3xl fade-up">
            <div className="eyebrow text-lime">
              <span className="editorial-rule bg-lime" /> Chapter III · {copy.eyebrow}
            </div>
            <h1 className="font-display text-white text-5xl md:text-7xl leading-[0.98] mt-6 whitespace-pre-line">
              {copy.headline}
            </h1>
            <p className="mt-6 text-cream/85 text-lg max-w-xl leading-relaxed">
              {copy.subline}
            </p>

            <div className="mt-10">
              <CountdownTimer variant="dark" />
            </div>

            <div className="mt-10">
              <a href="#register" className="btn-primary">
                Register for Chapter III
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Value strip */}
      <section className="py-16 bg-cream">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { v: "300 acres", l: "Private hill" },
            { v: "26+ acres", l: "Layout" },
            { v: "NA · RERA", l: "Classification" },
            { v: "Sept 12–13", l: "Launch, 2026" },
          ].map((s) => (
            <div key={s.l} className="border-l-2 border-forest pl-4">
              <div className="font-display text-forest text-3xl md:text-4xl leading-none">{s.v}</div>
              <div className="eyebrow text-forest/50 mt-3">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Registration form */}
      <section id="register" className="section-pad bg-offwhite">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10">
          <aside className="md:col-span-4">
            <div className="eyebrow text-teal mb-4"><span className="editorial-rule" /> Register</div>
            <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight">
              A guest list,<br />not a form.
            </h2>
            <p className="mt-6 text-forest/70 leading-relaxed max-w-md">
              We keep the list small on purpose. Three quick steps to help us
              prepare a visit tailored to you — not a mass-mailer.
            </p>
          </aside>
          <div className="md:col-span-7 md:col-start-6">
            <EventRegistrationForm source="eoi_landing" />
          </div>
        </div>
      </section>

      {/* Middle CTA */}
      <section className="py-16 bg-forest text-cream text-center">
        <div className="max-w-2xl mx-auto px-5">
          <h3 className="font-display text-3xl md:text-4xl leading-tight">
            Two golden-hour days.<br />One quiet ridge.
          </h3>
          <a href="#register" className="btn-primary mt-8">Register for Chapter III</a>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-cream text-center">
        <div className="max-w-2xl mx-auto px-5">
          <div className="eyebrow text-teal mb-4">Or, if you prefer</div>
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="btn-outline-forest">
            <MessageCircle size={16} /> WhatsApp us directly
          </a>
        </div>
      </section>
    </div>
  );
}
